import RefreshIcon from '@mui/icons-material/Refresh';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import type { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { cropImageFromElement, rotateImage90, urlToFile } from '../utils/cropAndRotateImage';

interface CropImageDialogProps {
  file: File | null;
  open: boolean;
  onApply: (file: File) => void;
  onSkip: () => void;
  onCancel: () => void;
}

export function CropImageDialog({
  file,
  open,
  onApply,
  onSkip,
  onCancel,
}: CropImageDialogProps): JSX.Element {
  const [workingUrl, setWorkingUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Initialize when file changes
  useEffect(() => {
    if (!open || !file) return;
    const url = URL.createObjectURL(file);
    setOriginalUrl(url);
    setWorkingUrl(url);
    setCrop(undefined);
    setCompletedCrop(null);
    setError(null);
    return () => {
      // cleanup blob URLs on next open (not this one, we still need it)
    };
  }, [open, file]);

  // Cleanup blob URLs when dialog closes
  useEffect(() => {
    if (!open && originalUrl) {
      URL.revokeObjectURL(originalUrl);
      setOriginalUrl('');
      setWorkingUrl('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRotate = useCallback(async () => {
    if (!workingUrl) return;
    setIsProcessing(true);
    setError(null);
    try {
      const rotated = await rotateImage90(workingUrl);
      setWorkingUrl(rotated);
      setCrop(undefined);
      setCompletedCrop(null);
    } catch {
      setError('No se pudo rotar la imagen.');
    } finally {
      setIsProcessing(false);
    }
  }, [workingUrl]);

  function handleReset(): void {
    setWorkingUrl(originalUrl);
    setCrop(undefined);
    setCompletedCrop(null);
    setError(null);
  }

  async function handleApply(): Promise<void> {
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    try {
      let result: File;
      const hasActiveCrop =
        completedCrop !== null && completedCrop.width > 0 && completedCrop.height > 0;

      if (hasActiveCrop && imgRef.current) {
        result = await cropImageFromElement(
          imgRef.current,
          completedCrop,
          file.name,
          file.type,
        );
      } else {
        // No crop selected — use the working URL (may be rotated)
        result = await urlToFile(workingUrl, file.name, file.type);
      }
      onApply(result);
    } catch {
      setError('No se pudo procesar la imagen. Intentá de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  }

  function handleSkip(): void {
    onSkip();
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={isProcessing ? undefined : onCancel}>
      <DialogTitle>Recortar imagen</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {workingUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              style={{ maxHeight: '60vh' }}
            >
              <img
                ref={imgRef}
                src={workingUrl}
                alt="Imagen a recortar"
                style={{ maxHeight: '60vh', maxWidth: '100%', display: 'block' }}
              />
            </ReactCrop>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={isProcessing ? <CircularProgress size={14} /> : <RotateRightIcon />}
            onClick={() => void handleRotate()}
            disabled={isProcessing}
          >
            Rotar 90°
          </Button>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={isProcessing}
          >
            Resetear
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleSkip} disabled={isProcessing}>
          Omitir crop
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleApply()}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Aplicar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
